import styled from "@emotion/styled";

import FlexContainer from "shared/components/FlexContainer";
import Icon from "shared/components/Icon";
import { COLORS, MEDIA_QUERIES } from "shared/config/constants";
import { AlignedCenterFlexPartial } from "shared/styles/commonStyles";

const commonCol = {
  ...AlignedCenterFlexPartial,
  padding: "12px 24px",
};

const headerHeight = "67px";

const secondColSmall = "380px";
const firstColSmall = `calc(100% - ${secondColSmall})`;

type Props = {
  isCancelledDeal: any;
  isDeclinedDeal: any;
  useDealsListUiRedesign?: boolean;
  isMobile?: boolean;
};

const getDealHeaderSecondColBg = (props: Props) => {
  if (props.isDeclinedDeal) {
    return COLORS.lightRed2;
  }

  if (props.isCancelledDeal) {
    return COLORS.lightGray4;
  }

  // Use matching dark purple when redesign flag is enabled
  if (props.useDealsListUiRedesign) {
    return COLORS.lightViolet; // #423973
  }

  return COLORS.violetTheme.lightViolet;
};

const DealHeaderContent = styled.div((props: Props) => ({
  ...AlignedCenterFlexPartial,
  color: COLORS.white,
  width: "100%",
  alignItems: "stretch",
  "& > div": {
    minHeight: headerHeight,
  },
  fontWeight: 500,
  ".deal-header-first-col": {
    ...commonCol,
    gap: "10px",
    flexWrap: "wrap",
    // width: firstColLarge,
    justifyContent: "space-between",
    flex: 1,
    backgroundColor: COLORS.lightViolet,
    borderRadius: "4px 0 0 4px",
    ...(props.useDealsListUiRedesign && !props.isMobile && {
      paddingLeft: "18px",
    }),
    ".deal-header-title-container": {
      ...AlignedCenterFlexPartial,
      marginRight: "18px",
      fontWeight: 600,
      fontSize: "17px",
      ".deal-header-title-text-wrapper": {
        maxWidth: "100%",
      },
    },
    ".integration-mark": {
      marginLeft: "23px",
      minWidth: "20px",

      [MEDIA_QUERIES.w.ltMd]: {
        marginLeft: "0px",
      },
    },
    ".deal-header-assign-unit": {
      marginLeft: "30px",
      whiteSpace: "nowrap",
      color: COLORS.violetTheme.lightGreen,

      [MEDIA_QUERIES.w.ltMd]: {
        marginLeft: "10px",
        marginRight: "30px",
      },
    },
  },
  ".deal-header-second-col": {
    ...commonCol,
    justifyContent: "space-between",
    backgroundColor: getDealHeaderSecondColBg(props),
    borderRadius: "0 4px 4px 0",
    ".deal-header-inactive": {
      ...AlignedCenterFlexPartial,
      span: {
        marginLeft: "16px",
        color: COLORS.white,
      },
      "&.cancelled": {
        span: {
          color: COLORS.gray2,
        },
      },
    },
    ".deal-header-actions": {
      ...AlignedCenterFlexPartial,
      ".deal-header-pdf-download": {
        marginRight: "30px",
      },
    },
  },
  [MEDIA_QUERIES.w.ltXl]: {
    ".deal-header-first-col": {
      width: firstColSmall,
    }
  },
  [MEDIA_QUERIES.w.ltMd]: {
    flexDirection: "column",
    height: "auto",
    maxHeight: "none",
    ".deal-header-first-col": {
      alignItems: "stretch",
      flexDirection: "column",
      gap: 0,
      width: "100%",
      borderRadius: 0,
      paddingTop: 0,
      paddingBottom: 0,
      ".deal-header-title-container": {
        width: "100%",
        minHeight: "67px",
        margin: 0,
        paddingRight: "20px",
        ".dropdown-chevron": {},
        "&.deal-header-title-no-unit": {
          justifyContent: "space-between",
          paddingRight: 0,
        },
      },
      ".integration-mark": {
        marginLeft: "10px",
      },
    },
    ".cosign-badge-col": {
      width: "100%",
      padding: "12px 24px",
      justifyContent: "flex-start",
    },
    ".deal-header-second-col": {
      borderRadius: 0,
      width: "100%",
      minHeight: "61px",
      height: "61px",
      backgroundColor: COLORS.background,
      color: COLORS.grayTheme.activeTextColor,
      ".deal-header-inactive": {
        span: {
          color: COLORS.primary,
        },
      },
      ".deal-header-pdf-download": {
        display: "none",
      },
    },
  },
}));

const DealUnitHeaderItemsContent = styled(FlexContainer)({
  ...AlignedCenterFlexPartial,
  // flexWrap: "wrap",
  justifyContent: "flex-end",
  // fontWeight: 400,
  ".deal-rent": {
    textAlign: "end",
  },
  ".edit-rent-icon": {
    marginLeft: "8px",
    cursor: "pointer",
  },
});

const UnitPopoverContent = styled.div({
  padding: "16px",
  minWidth: "250px",
  ".popover-title": {
    fontSize: "12px",
    fontWeight: 600,
    marginBottom: "16px",
  },
  ".popover-items": {
    border: "none",
    p: {
      margin: 0,
    },
    "p + p": {
      marginTop: "10px",
    },
  },
  ".application-title": {
    marginTop: "16px",
  },
});

const TitlePopoverContent = styled.div({
  padding: "16px",
  maxWidth: "290px",
  ".popover-title": {
    marginBottom: "6px",
  },
});

const DealHeaderDropdownTrigger = styled.div({
  width: 37,
  height: 37,
  borderRadius: "50%",
  backgroundColor: "#423973",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  "& svg": {
    transform: "rotate(90deg)",
  },
  "& svg circle:nth-of-type(n+2)": {
    fill: COLORS.white,
  },
});

const InfoDark = styled(Icon.InfoDark)`
  cursor: pointer;
  margin-left: 20px;
`;

const AffordableBadge = styled.div({
  backgroundColor: COLORS.dashboard.lightViolet2,
  borderRadius: "4px",
  color: COLORS.black,
  fontSize: "14px",
  fontWeight: 700,
  lineHeight: 1,
  marginLeft: "16px",
  padding: "10px 14px",
  whiteSpace: "nowrap",
});

const MainText = styled.div({
  whiteSpace: "nowrap",
  '& > a': {
    color: COLORS.white
  },
  '& > a:hover': {
    textDecoration: 'underline'
  }
});

const CosignEnabledBadge = styled.div({
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  backgroundColor: "#2563EB",
  borderRadius: "24px",
  padding: "8px 16px",
  marginBottom: "8px",
  fontWeight: 600,
  fontSize: "14px",
  color: COLORS.white,
  whiteSpace: "nowrap",
  ".cosign-shield-icon": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  ".cosign-check-icon": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "22px",
    height: "22px",
    borderRadius: "50%",
    border: "2px solid #D4AF37",
    backgroundColor: "transparent",
  },
});

// CosignBadge - expandable badge with dropdown
type CosignBadgeContainerProps = {
  isEnabled: boolean;
  isOpen?: boolean;
};

const CosignBadgeContainer = styled.div({
  position: "relative",
  display: "inline-flex",
});

const CosignBadgePill = styled.div<CosignBadgeContainerProps>(({ isEnabled, isOpen }) => ({
  display: "inline-flex",
  alignItems: "center",
  marginLeft: "8px",
  gap: "0px",
  // backgroundColor: isEnabled ? "#2563EB" : "#4B5563",
  backgroundColor: "white",
  borderRadius: "2px",
  // padding: "4px 6px",
  fontWeight: 600,
  fontSize: "14px",
  color: COLORS.white,
  whiteSpace: "nowrap",
  cursor: "pointer",
  transition: "all 0.2s ease",
  overflow: "hidden",
  // border: isEnabled ? "2px solid #60A5FA" : "2px solid #6B7280",
  ".cosign-badge-icon": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  ".cosign-badge-text": {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    maxWidth: isOpen ? "150px" : "0px",
    opacity: isOpen ? 1 : 0,
    overflow: "hidden",
    transition: "max-width 0.3s ease, opacity 0.2s ease, margin-left 0.2s ease",
    marginLeft: isOpen ? "8px" : "0px",
  },
  ".cosign-badge-chevron": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform 0.2s ease",
    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
  },
  "&:hover": {
    opacity: 0.9,
  },
}));

const CosignDropdownContainer = styled.div<CosignBadgeContainerProps>(({ isEnabled }) => ({
  position: "absolute",
  // top: "calc(100% + 8px)",
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 1000,
  minWidth: "340px",
  backgroundColor: COLORS.white,
  // borderRadius: "12px",
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
  overflow: "hidden",
  transition: "opacity 0.2s ease, visibility 0.2s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-6px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "12px",
    height: "12px",
    backgroundColor: isEnabled ? "#2563EB" : "#4B5563",
    clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)",
  },
}));

const CosignDropdownHeader = styled.div<CosignBadgeContainerProps>(({ isEnabled }) => ({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "16px 20px",
  backgroundColor: isEnabled ? "#2563EB" : "#4B5563",
  color: COLORS.white,
  fontWeight: 600,
  fontSize: "16px",
  ".cosign-dropdown-person-icon": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0.9,
  },
}));

const CosignDropdownBody = styled.div({
  padding: "20px",
});

const CosignDropdownDescription = styled.p({
  margin: "0 0 16px 0",
  fontSize: "14px",
  lineHeight: 1.5,
  color: "#4B5563",
});

const CosignDropdownBenefitsList = styled.ul({
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: "12px",
});

const CosignDropdownBenefitItem = styled.li({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  fontSize: "14px",
  color: "#374151",
  fontWeight: 500,
  ".cosign-benefit-icon": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#16A34A",
    flexShrink: 0,
  },
});

const CosignDropdownFooter = styled.div({
  padding: "16px 20px",
  borderTop: "1px solid #E5E7EB",
});

const CosignOnboardButton = styled.button({
  width: "100%",
  padding: "12px 20px",
  backgroundColor: "#2563EB",
  color: COLORS.white,
  border: "none",
  borderRadius: "8px",
  fontSize: "15px",
  fontWeight: 600,
  cursor: "pointer",
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "#1D4ED8",
  },
});

const CosignLearnMoreLink = styled.a({
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  color: "#2563EB",
  fontSize: "14px",
  fontWeight: 500,
  textDecoration: "none",
  cursor: "pointer",
  "&:hover": {
    textDecoration: "underline",
  },
});


export {
  AffordableBadge,
  DealHeaderContent,
  DealUnitHeaderItemsContent,
  UnitPopoverContent,
  TitlePopoverContent,
  DealHeaderDropdownTrigger,
  InfoDark,
  MainText,
  CosignEnabledBadge,
  CosignBadgeContainer,
  CosignBadgePill,
  CosignDropdownContainer,
  CosignDropdownHeader,
  CosignDropdownBody,
  CosignDropdownDescription,
  CosignDropdownBenefitsList,
  CosignDropdownBenefitItem,
  CosignDropdownFooter,
  CosignOnboardButton,
  CosignLearnMoreLink,
};
